import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { calculatePriceBreakdown, formatCurrency } from '@/lib/pricing';
import { rollReviewRepository } from '../repositories/supabaseRollReviewRepository';
import { rollReviewApi } from '../services/rollReviewApi';

export interface RollReviewPaymentBreakdown {
  amountCents: number;
  instructorPriceCents: number;
  serviceFeeCents: number;
  totalChargedCents: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  netToInstructorCents: number;
  currency: string;
  amountFormatted: string;
  serviceFeeFormatted: string;
  totalChargedFormatted: string;
  platformFeeFormatted: string;
  netToInstructorFormatted: string;
}

export interface RollReview {
  id: string;
  practitioner_id: string;
  instructor_id: string;
  payment_id: string | null;
  price_cents: number;
  currency: string;
  status: 'pending_payment' | 'paid' | 'in_review' | 'delivered' | 'expired' | 'refunded' | 'cancelled';
  turnaround_hours: number;
  deadline_at: string | null;
  delivered_at: string | null;
  request_video_url: string | null;
  request_notes: string | null;
  training_video_id: string | null;
  created_at: string;
}

/**
 * Compute price breakdown for display before checkout.
 */
export function useRollReviewBreakdown(
  priceCents: number,
  currency: string,
  countryCode: string = 'US'
): RollReviewPaymentBreakdown {
  const breakdown = calculatePriceBreakdown(priceCents, countryCode);
  return {
    amountCents:              priceCents,
    instructorPriceCents:     breakdown.instructorPriceCents,
    serviceFeeCents:          breakdown.serviceFeeCents,
    totalChargedCents:        breakdown.practitionerTotalCents,
    platformFeeCents:         breakdown.platformCommissionCents,
    stripeFeeCents:           breakdown.stripeFeesCents,
    netToInstructorCents:     breakdown.netToInstructorCents,
    currency,
    amountFormatted:          formatCurrency(priceCents, currency),
    serviceFeeFormatted:      formatCurrency(breakdown.serviceFeeCents, currency),
    totalChargedFormatted:    formatCurrency(breakdown.practitionerTotalCents, currency),
    platformFeeFormatted:     formatCurrency(breakdown.platformCommissionCents, currency),
    netToInstructorFormatted: formatCurrency(breakdown.netToInstructorCents, currency),
  };
}

/**
 * Fetch a single roll_review by ID (for post-payment status polling).
 */
export function useRollReview(rollReviewId: string | null) {
  return useQuery({
    queryKey: ['roll-review', rollReviewId],
    queryFn: async (): Promise<RollReview | null> => {
      if (!rollReviewId) return null;
      return rollReviewRepository.fetchRollReviewById(rollReviewId);
    },
    enabled: !!rollReviewId,
    refetchInterval: (query) => {
      // Poll every 3s while status is still pending_payment (waiting for webhook)
      const data = query.state.data as RollReview | null;
      return data?.status === 'pending_payment' ? 3000 : false;
    },
  });
}

/**
 * Initiate checkout: create roll_review + payment records, redirect to Stripe.
 */
export function useCreateCheckoutSession() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instructorId,
      priceCents,
      currency,
      turnaroundHours = 48,
    }: {
      instructorId: string;
      priceCents: number;
      currency: string;
      turnaroundHours?: number;
    }) => {
      if (!user) throw new Error('Not authenticated');
      return rollReviewApi.createCheckoutSession({
        instructorId,
        priceCents,
        currency,
        turnaroundHours,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roll-reviews'] });
    },
  });
}

/**
 * Create a payment intent for a roll review (embedded payment element flow).
 */
export function useCreatePaymentIntent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instructor_id,
      training_video_id,
      turnaround_hours = 72,
      save_for_future = false,
    }: {
      instructor_id: string;
      training_video_id: string;
      turnaround_hours?: number;
      save_for_future?: boolean;
    }) => {
      if (!user) throw new Error('Not authenticated');
      return rollReviewApi.createPaymentIntent({
        instructor_id,
        training_video_id,
        turnaround_hours,
        save_for_future,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roll-reviews'] });
    },
  });
}

/**
 * Instructor delivers a review.
 */
export function useDeliverReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rollReviewId,
      feedbackText,
      feedbackVideoUrl,
      trainingVideoId,
    }: {
      rollReviewId:     string;
      feedbackText?:    string;
      feedbackVideoUrl?: string;
      trainingVideoId?: string;
    }) => {
      return rollReviewApi.deliverReviewWithFeedback({
        rollReviewId,
        feedbackText,
        feedbackVideoUrl,
        trainingVideoId,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roll-review', variables.rollReviewId] });
      queryClient.invalidateQueries({ queryKey: ['instructor-reviews'] });
    },
  });
}
